"use client"

import { Command as CommandPrimitive } from "cmdk"
import { ChevronDown, Search, X } from "lucide-react"
import React, {
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"

import { cn } from "../../helpers"

import { Label } from "../Label/label"
import { Skeleton } from "../Skeleton/skeleton"
import * as styles from "./auto-complete.styled"
import * as inputStyles from "./input.styled"
import { CheckSolid } from "../../icons"

type Option = Record<"value" | "label", string> & Record<string, unknown>

type Placement = "top" | "bottom"

type AutoCompleteProps<T extends Option> = {
  options: T[]
  emptyMessage: string
  label?: string
  value?: T
  onValueChange?: (value: T) => void
  placeholder?: string
  disabled?: boolean
  clearIfOptionSelected?: boolean
  isLoading?: boolean
  onInputChange?: (value: string) => void
  inputValue?: string
  mode?: "default" | "dynamic"
  onBlur?: (event: FocusEvent<HTMLInputElement, Element>) => void
  name?: string
  hasError?: boolean
  extra?: React.ReactNode
  placement?: Placement
  filter?: (value: string, search: string) => number
  separatorAfterValue?: string
}

export const AutoComplete = <T extends Option>({
  options,
  placeholder,
  emptyMessage,
  label,
  value,
  onValueChange,
  disabled,
  clearIfOptionSelected,
  isLoading,
  onInputChange,
  inputValue: inputValueProp,
  mode = "default",
  onBlur,
  name,
  hasError,
  placement = "bottom",
  extra,
  filter: filterProp,
  separatorAfterValue,
}: AutoCompleteProps<T>) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const wasClearedRef = useRef(false)
  const skipInputChangeRef = useRef(false)
  const filterRef = useRef(filterProp)
  filterRef.current = filterProp

  const [isOpen, setOpen] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [selected, setSelected] = useState<Option>(value as Option)
  const [inputValue, setInputValue] = useState<string>(
    inputValueProp || value?.label || ""
  )

  const isInputReadyAndFilled = (inputRef.current?.value?.length || 0) > 0
  const isOptionsReadyToBeShown =
    mode === "dynamic" ? isInputReadyAndFilled : true

  const popperPositionClass =
    placement === "top" ? "bottom-full mb-2" : "top-full mt-1"

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const input = inputRef.current
      if (!input) {
        return
      }

      // Keep the options displayed when the user is typing
      if (!isOpen) {
        setOpen(true)
      }

      // This is not a default behaviour of the <input /> field
      if (event.key === "Enter" && input.value !== "") {
        const optionToSelect = options.find(
          (option) => option.label === input.value
        )
        if (optionToSelect) {
          setSelected(optionToSelect)
          onValueChange?.(optionToSelect)
        }
      }

      if (event.key === "Escape") {
        input.blur()
      }
    },
    [isOpen, options, onValueChange]
  )

  const handleBlur = useCallback(() => {
    setOpen(false)
    setIsFocused(false)

    if (wasClearedRef.current) {
      setInputValue("")
      setSelected({} as Option)
      wasClearedRef.current = false
    } else {
      skipInputChangeRef.current = true
      setInputValue(selected?.label)
      if (selected?.value) {
        onValueChange?.(selected as T)
      }
    }
  }, [selected, onValueChange])

  const handleSelectOption = useCallback(
    (selectedOption: T) => {
      wasClearedRef.current = false
      skipInputChangeRef.current = true
      setInputValue(selectedOption.label)

      setSelected(selectedOption)
      onValueChange?.(selectedOption)

      // This is a hack to prevent the input from being focused after the user selects an option
      // We can call this hack: "The next tick"
      setTimeout(() => {
        inputRef?.current?.blur()
      }, 0)
    },
    [onValueChange]
  )

  const handleClear = useCallback(() => {
    wasClearedRef.current = true
    setInputValue("")
    setSelected({} as Option)
    onInputChange?.("")
    setOpen(true)

    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }, [onInputChange])

  // Clear field if `clearIfOptionSelected` is true
  // Useful when you want to clear the field after the user selects an option
  useEffect(() => {
    if (clearIfOptionSelected) {
      setSelected({} as Option)
      setInputValue("")
    }
  }, [clearIfOptionSelected])

  return (
    <CommandPrimitive
      onKeyDown={handleKeyDown}
      onFocus={(event) => {
        event.stopPropagation()

        setInputValue(inputRef.current?.value || "")
      }}
      className={styles.commandWrapper}
      filter={(value, search) => {
        if (filterRef.current) return filterRef.current(value, search)
        return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
      }}
    >
      <label className={inputStyles.wrapper()}>
        {label ? <Label>{label}</Label> : null}
        <div
          className={cn(
            inputStyles.inputVariants({
              disabled,
              variant: hasError ? "error" : "default",
            })
          )}
        >
          <Search className={styles.searchIcon(disabled)} />
          <CommandPrimitive.Input
            name={name}
            ref={inputRef}
            value={inputValue}
            onValueChange={(value) => {
              if (skipInputChangeRef.current) {
                skipInputChangeRef.current = false
                return
              }
              if (mode === "dynamic") {
                onInputChange?.(value)
                setInputValue(value)
                return
              }
              if (isLoading) {
                return
              }
              onInputChange?.(value)
              setInputValue(value)
            }}
            onBlur={(event) => {
              onBlur?.(event)
              handleBlur()
            }}
            onFocus={() => {
              skipInputChangeRef.current = false
              setIsFocused(true)
              setOpen(true)
            }}
            placeholder={placeholder}
            className={inputStyles.basicInput}
            disabled={disabled}
          />
          {isFocused && !disabled ? (
            <button
              type="button"
              aria-label="Clear"
              onMouseDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleClear()
              }}
              className={cn(
                "ml-2 inline-flex shrink-0 items-center justify-center rounded-full p-0",
                "aspect-square transition-colors h-5 w-5",
                inputValue?.length
                  ? "bg-neutral-600 text-white hover:bg-neutral-600"
                  : "bg-neutral-400 text-white hover:bg-neutral-300"
              )}
            >
              <X className="h-3 w-3" strokeWidth={3.5} />
            </button>
          ) : (
            <ChevronDown className={styles.chevronIcon(isOpen, disabled)} />
          )}
        </div>
      </label>
      <div className={cn(styles.resultWrapper, placement === "top" && "static")}>
        {isOpen && isOptionsReadyToBeShown ? (
          <div
            className={cn(
              "animate-in fade-in-0 zoom-in-95 absolute z-10 w-full rounded-xl border-2 border-neutral-200 bg-white shadow-sm outline-none",
              popperPositionClass
            )}
          >
            <CommandPrimitive.List>
              {isLoading ? (
                <CommandPrimitive.Loading>
                  <div className={styles.commandItem(false)}>
                    <Skeleton className="h-5 w-32" />
                  </div>
                </CommandPrimitive.Loading>
              ) : null}
              {options.length > 0 && !isLoading ? (
                <CommandPrimitive.Group
                  className={styles.commandGroup}
                  data-ci="autocomplete-list"
                >
                  {options.map((option) => {
                    const isSelected = selected?.value === option.value
                    return (
                      <CommandPrimitive.Item
                        key={option.value}
                        value={option.label}
                        onMouseDown={(event) => {
                          event.preventDefault()
                          event.stopPropagation()
                        }}
                        onSelect={() => handleSelectOption(option)}
                        className={cn(
                          styles.commandItem(isSelected),
                          separatorAfterValue != null &&
                            option.value === separatorAfterValue &&
                            "mb-4 after:absolute after:inset-x-4 after:-bottom-2.5 after:h-px after:bg-neutral-100"
                        )}
                        data-ci="autocomplete-item"
                      >
                        {option.label}

                        {isSelected ? (
                          <CheckSolid className={styles.selectedIcon} />
                        ) : null}
                      </CommandPrimitive.Item>
                    )
                  })}
                </CommandPrimitive.Group>
              ) : null}

              <CommandPrimitive.Empty
                className={styles.commandEmpty(isLoading)}
                data-ci="autocomplete-empty"
              >
                {emptyMessage}
              </CommandPrimitive.Empty>
              {isLoading ? null : extra}
            </CommandPrimitive.List>
          </div>
        ) : null}
      </div>
    </CommandPrimitive>
  )
}
